// @ts-check
/**
 * Shared model for multi-select filters: groups of tickable options, plus a selection.
 * The group carries everything data-specific, so the popup, chips and matching are the
 * same code whatever is filtered.
 *
 * @module filterUtils
 */

/**
 * One tickable option within a group.
 * @typedef {Object} FilterOption
 * @property {string} value  What is stored in the selection, and matched against the item.
 * @property {string} label  What the reader sees, e.g. "At Risk".
 */

/**
 * A group of options, shown as one titled section in the popup.
 *
 * @typedef {Object} FilterGroup
 * @property {string} key           Key this group's selected values are stored under.
 * @property {string} label         Section heading, e.g. "Risk Level".
 * @property {FilterOption[]} options
 * @property {(item: any) => string[]} getValues  The item's values for this group.
 * @property {string} [chipPrefix]  For values that are ambiguous alone ("At Risk" -> "Risk: At Risk").
 */

/**
 * Selected values, keyed by group. A missing or empty group filters nothing — not the
 * same as "match nothing".
 * @typedef {Record<string, string[]>} FilterSelection
 */

/** @type {FilterSelection} */
export const EMPTY_SELECTION = {};

/**
 * The number on the Filter badge: ticked values across every group, not groups.
 *
 * @param {FilterSelection} [selection]
 * @returns {number}
 */
export const countSelected = (selection = {}) =>
  Object.values(selection).reduce((total, values) => total + (values?.length ?? 0), 0);

/**
 * @param {FilterSelection} selection
 * @param {string} groupKey
 * @param {string} value
 * @returns {boolean}
 */
export const isSelected = (selection, groupKey, value) =>
  (selection?.[groupKey] ?? []).includes(value);

/**
 * Tick or untick one value, returning a new selection. A group emptied by the last untick
 * is dropped, so an untouched selection and a cleared one have the same shape.
 *
 * @param {FilterSelection} selection
 * @param {string} groupKey
 * @param {string} value
 * @returns {FilterSelection}
 */
export const toggleValue = (selection, groupKey, value) => {
  const current = selection?.[groupKey] ?? [];
  const next = current.includes(value)
    ? current.filter((selected) => selected !== value)
    : [...current, value];

  const rest = { ...selection };
  delete rest[groupKey];

  return next.length > 0 ? { ...rest, [groupKey]: next } : rest;
};

/**
 * Remove one value, returning a new selection. What an individual chip's '×' does.
 *
 * @param {FilterSelection} selection
 * @param {string} groupKey
 * @param {string} value
 * @returns {FilterSelection}
 */
export const removeValue = (selection, groupKey, value) =>
  isSelected(selection, groupKey, value) ? toggleValue(selection, groupKey, value) : selection;

/**
 * The chips to render, in the groups' own order so they do not reshuffle as values are ticked. 
 * Values no group still offers are dropped - otherwise a chip could filter on a category the popup can no longer untick.
 *
 * @param {FilterGroup[]} [groups]
 * @param {FilterSelection} [selection]
 * @returns {{ groupKey: string, value: string, label: string }[]}
 */
export const listActiveFilters = (groups = [], selection = {}) =>
  groups.flatMap((group) =>
    (group.options ?? [])
      .filter((option) => isSelected(selection, group.key, option.value))
      .map((option) => ({
        groupKey: group.key,
        value: option.value,
        label: group.chipPrefix ? `${group.chipPrefix}: ${option.label}` : option.label,
      }))
  );

/**
 * Whether an item survives the current selection. OR within a group, AND across groups:
 * another tick in a group widens the result, another group narrows it.
 *
 * @param {any} item
 * @param {FilterGroup[]} [groups]
 * @param {FilterSelection} [selection]
 * @returns {boolean}
 */
export const matchesFilters = (item, groups = [], selection = {}) =>
  groups.every((group) => {
    const selected = selection?.[group.key] ?? [];
    if (selected.length === 0) return true;

    const values = group.getValues?.(item) ?? [];
    return values.some((value) => selected.includes(value));
  });
